import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import TripHeader from './components/TripHeader';
import TripMetadata from './components/TripMetadata';
import DayItinerary from './components/DayItinerary';
import TripMap from './components/TripMap';
import ShareModal from './components/ShareModal';
import Icon from '../../components/AppIcon';
import StateIcon from './components/StateIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { tripService } from '../../services/tripService';
import { itineraryService } from '../../services/itineraryService';
import { tripPDFService } from '../../services/tripPDFService';
import { useAuth } from '../../contexts/AuthContext';
import WeatherForecast from './components/WeatherForecast';
import PackingList from './components/PackingList';
// ✅ NEW: Import Google Maps Place Service
import { getMultipleParkPhotos, getFallbackParkImage } from '../../services/googleMapsPlaceService';
import { generateTripStructuredData, generateBreadcrumbStructuredData } from '../../utils/seoUtils';
import SEOHead from '../../components/SEOHead';


const TripDetailView = () => {
  const { tripId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading } = useAuth();
  const [expandedDays, setExpandedDays] = useState({});
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isLoadingTrip, setIsLoadingTrip] = useState(true);
  const [error, setError] = useState('');
  const [trip, setTrip] = useState(null);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedTrip, setEditedTrip] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [rawDates, setRawDates] = useState({ startDate: null, endDate: null });
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [parkImages, setParkImages] = useState({});
  const [isLoadingImages, setIsLoadingImages] = useState(false);

  // Check if this is a newly created trip from confirmation
  const isNewTrip = location?.state?.isNewTrip;
  const passedTripData = location?.state?.tripData;
  const shouldShowConfirmation = location?.state?.showConfirmationMessage;
  // ✅ NEW: Check if we should generate PDF
  const shouldGeneratePDF = location?.state?.generatePDF;
  const returnToDashboard = location?.state?.returnToDashboard;

  // Get trip ID from URL params or query params (for backward compatibility)
  const actualTripId = tripId || searchParams?.get('id');

  // Define dropdown options
  const transportationOptions = [
    { label: 'Personal Car', value: 'Personal Car' },
    { label: 'RV', value: 'RV' },
    { label: 'Rental Car', value: 'Rental Car' },
    { label: 'Van', value: 'Van' },
    { label: 'Flight', value: 'Flight' },
    { label: 'Other', value: 'Other' }
  ];

  const accommodationOptions = [
    { label: 'Camping', value: 'Camping' },
    { label: 'Car Camping', value: 'Car Camping' },
    { label: 'Backcountry', value: 'Backcountry' },
    { label: 'Hotel', value: 'Hotel' },
    { label: 'Motel', value: 'Motel' },
    { label: 'Resort', value: 'Resort' },
    { label: 'Other', value: 'Other' }
  ];

  // ✅ NEW: PDF generation effect
  useEffect(() => {
    const generatePDFSnapshot = async () => {
      if (!shouldGeneratePDF || !trip || isGeneratingPDF) return;

      setIsGeneratingPDF(true);
      setShowConfirmationMessage(false); // Hide any existing messages

      try {
        // ✅ CRITICAL FIX: Expand ALL days before generating PDF
        if (trip?.days?.length > 0) {
          const allDaysExpanded = {};
          trip?.days?.forEach(day => {
            allDaysExpanded[day?.id] = true;
          });
          setExpandedDays(allDaysExpanded);
          
          // ✅ CRITICAL: Wait longer for DOM to fully render all expanded content
          await new Promise(resolve => setTimeout(resolve, 1500));
        }

        // Generate PDF from current page
        const pdfBlob = await tripPDFService?.generateVisualSnapshotPDF('trip-detail-container');

        // Trigger download
        const url = URL?.createObjectURL(pdfBlob);
        const link = document?.createElement('a');
        link.href = url;
        link.download = `${trip?.title || trip?.destination || 'trip'}-snapshot-${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
        document?.body?.appendChild(link);
        link?.click();
        document?.body?.removeChild(link);
        URL?.revokeObjectURL(url);

        // Log activity
        if (user?.id) {
          await tripService?.logActivity(
            user?.id,
            trip?.id,
            'downloaded',
            'Downloaded trip snapshot PDF',
            {
              downloaded_at: new Date()?.toISOString(),
              location: 'trip_detail_view',
              type: 'visual_snapshot'
            }
          );
        }

        // Show success message
        setShowConfirmationMessage(true);
        setTimeout(() => {
          setShowConfirmationMessage(false);
          
          // Navigate back to dashboard if requested
          if (returnToDashboard) {
            navigate('/user-dashboard');
          }
        }, 2000);

      } catch (err) {
        console.error('PDF snapshot generation error:', err);
        setError(`Failed to generate PDF: ${err?.message || 'Please try the download button instead'}`);
        
        // Navigate back to dashboard after showing error
        if (returnToDashboard) {
          setTimeout(() => navigate('/user-dashboard'), 3000);
        }
      } finally {
        setIsGeneratingPDF(false);
      }
    };

    if (shouldGeneratePDF && trip && !isLoadingTrip) {
      generatePDFSnapshot();
    }
  }, [shouldGeneratePDF, trip, isLoadingTrip, returnToDashboard, navigate, user, isGeneratingPDF]);

  // ✅ NEW: Handle title update from TripHeader
  const handleTitleUpdate = async (newTitle) => {
    if (!trip?.id || !newTitle?.trim()) return;

    try {
      // Update in database
      const updatedTrip = await tripService?.updateTrip(trip?.id, {
        title: newTitle?.trim()
      });

      // Update local state - ✅ CRITICAL FIX: Ensure we use the returned title from database
      setTrip(prev => ({
        ...prev,
        title: updatedTrip?.title || newTitle?.trim()
      }));

      // ✅ NEW: Also update the raw display title immediately for consistency
      if (updatedTrip?.title) {
        // Force re-render by updating state
        setTrip(prev => ({ ...prev, title: updatedTrip?.title }));
      }

      // Show success message
      setShowConfirmationMessage(true);
      setTimeout(() => setShowConfirmationMessage(false), 3000);

    } catch (err) {
      console.error('Error updating trip title:', err);
      throw err; // Let TripHeader handle the error
    }
  };

  useEffect(() => {
    const loadTripData = async () => {
      // Enhanced authentication check
      if (!isAuthenticated && !loading) {
        console.log('User not authenticated, redirecting to login');
        navigate('/user-login');
        return;
      }

      if (loading) {
        console.log('Auth still loading, waiting...');
        return;
      }

      // Enhanced trip ID validation
      if (!actualTripId) {
        console.error('No trip ID provided');
        setError('No trip ID provided. Please select a valid trip from your dashboard.');
        setIsLoadingTrip(false);
        return;
      }

      // Validate trip ID format (should be UUID)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex?.test(actualTripId)) {
        console.error('Invalid trip ID format:', actualTripId);
        setError('Invalid trip ID format. Please check the URL and try again.');
        setIsLoadingTrip(false);
        return;
      }

      setIsLoadingTrip(true);
      setError('');

      try {
        let tripData = null;
        let itineraryData = [];
        let aiData = null; // Store AI trip data

        console.log('Loading trip data for ID:', actualTripId);

        // If trip data was passed directly (from trip confirmation)
        if (passedTripData && isNewTrip) {
          console.log('Using passed trip data from navigation');
          tripData = passedTripData;
          aiData = passedTripData?.aiTripData; // Get AI data from passed trip
          
          // ✅ CRITICAL FIX: Always fetch itinerary from database even for new trips
          if (actualTripId) {
            try {
              itineraryData = await itineraryService?.getTripItinerary(actualTripId);
              console.log('Fetched itinerary for new trip:', itineraryData?.length, 'items');
            } catch (itineraryError) {
              console.warn('Could not load itinerary for new trip:', itineraryError?.message);
              // ✅ NEW: Set empty array instead of failing
              itineraryData = [];
            }
          }
        }
        // If we have a trip ID, fetch from database
        else if (actualTripId) {
          console.log('Fetching trip from database...');
          tripData = await tripService?.getTripById(actualTripId);
          
          // ✅ CRITICAL FIX: Log the fetched trip data for debugging
          console.log('Fetched trip data:', tripData);
          
          aiData = tripData?.aiTripData; // Get AI data from database
          
          // ✅ CRITICAL FIX: Try multiple ways to get itinerary data
          if (tripData?.itinerary && Array.isArray(tripData?.itinerary)) {
            itineraryData = tripData?.itinerary;
            console.log('Using itinerary from trip data:', itineraryData?.length, 'items');
          } else {
            try {
              itineraryData = await itineraryService?.getTripItinerary(actualTripId);
              console.log('Fetched itinerary separately:', itineraryData?.length, 'items');
            } catch (itineraryError) {
              console.error('Could not load itinerary:', itineraryError?.message);
              // ✅ NEW: Set empty array and show warning but don't fail
              itineraryData = [];
              console.warn('Trip will be displayed without itinerary details');
            }
          }
        }

        if (!tripData) {
          console.error('No trip data found');
          setError('Trip not found. It may have been deleted or you may not have permission to view it.');
          return;
        }

        // ✅ CRITICAL FIX: Log itinerary conversion process
        console.log('Converting itinerary to days format...');
        console.log('Raw itinerary data:', itineraryData);
        console.log('Trip start date:', tripData?.startDate);

        console.log('Successfully loaded trip data:', tripData);
        console.log('AI trip data available:', !!aiData);

        // Store raw dates for editing
        setRawDates({
          startDate: tripData?.startDate,
          endDate: tripData?.endDate
        });

        // ✅ CRITICAL FIX: Use creator's first name from database instead of logged-in user
        const creatorFirstName = tripData?.creatorFirstName || 'Trip Planner';

        // ✅ CRITICAL FIX: Convert itinerary with proper error handling AND AI data
        const convertedDays = convertItineraryToDays(itineraryData, tripData?.startDate, aiData);
        console.log('Converted days:', convertedDays);
        console.log('Number of days with activities:', convertedDays?.length);

        // Convert database format to component format
        const componentTrip = {
          id: tripData?.id,
          title: tripData?.title || `${tripData?.destination || tripData?.park} Adventure`,
          destination: tripData?.destination,
          park: tripData?.park,
          state: tripData?.state,
          city: tripData?.city,
          // ✅ CRITICAL FIX: Strip "X-day" or "X days" prefix from description
          description: tripData?.description 
            ? tripData?.description?.replace(/^\d+[\s-]day(s)?\s+/i, '')?.replace(/^Experience\s+/i, 'Experience ') 
            : `Experience ${tripData?.destination || tripData?.park} with this carefully planned adventure.`,
          createdAt: formatDate(tripData?.createdAt, true),
          lastModified: formatDate(tripData?.updatedAt, true),
          createdBy: creatorFirstName,
          primaryPark: tripData?.park || tripData?.destination,
          accommodation: tripData?.accommodation || 'Camping',
          transportation: 'Personal Vehicle',
          totalActivities: itineraryData?.length || 0,
          totalMiles: estimateMiles(tripData?.duration),
          coordinates: getDefaultCoordinates(tripData?.park),
          drivingDirections: `Navigate to ${tripData?.destination || tripData?.park}`,
          travelTime: estimateTravelTime(tripData?.duration),
          parkAddress: `${tripData?.destination || tripData?.park} Visitor Center`,
          days: convertedDays,
          aiTripData: aiData,
          featuredParks: aiData?.featuredParks || [tripData?.park || tripData?.destination],
          activityTypes: aiData?.activityTypes || tripData?.activities || [],
          accommodations: aiData?.accommodations,
          dining: aiData?.dining,
          reservationInfo: aiData?.reservationInfo,
          weatherForecast: aiData?.weatherForecast,
          isPublic: tripData?.isPublic || false,
          // ✅ CRITICAL FIX: Map database fields correctly for basic trip info display
          duration: tripData?.duration || calculateDuration(tripData?.startDate, tripData?.endDate),
          startDate: formatDate(tripData?.startDate),
          endDate: formatDate(tripData?.endDate),
          participants: tripData?.travelers || 2,
          estimatedCost: tripData?.total_cost?.toString() || '500'
        };

        console.log('Final component trip object:', componentTrip);
        setTrip(componentTrip);
        
        // Show confirmation message for newly created trips
        if (shouldShowConfirmation) {
          setShowConfirmationMessage(true);
          setTimeout(() => setShowConfirmationMessage(false), 5000);
        }

      } catch (err) {
        console.error('Error loading trip:', err);
        
        // Enhanced error handling with specific messages
        let errorMessage = err?.message || 'Failed to load trip data.';
        
        if (err?.message?.includes('not found')) {
          errorMessage = 'This trip was not found. It may have been deleted or you may not have permission to view it.';
        } else if (err?.message?.includes('Authentication required')) {
          errorMessage = 'Please log in to view trip details.';
          // Redirect to login after showing error
          setTimeout(() => navigate('/user-login'), 2000);
        } else if (err?.message?.includes('Network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
        
        setError(errorMessage);
      } finally {
        setIsLoadingTrip(false);
      }
    };

    loadTripData();
  }, [actualTripId, passedTripData, isAuthenticated, loading, navigate, user, isNewTrip, shouldShowConfirmation]);

  // ✅ NEW: Effect to fetch park images when trip data is loaded
  useEffect(() => {
    const fetchParkImages = async () => {
      if (!trip || !trip?.days || trip?.days?.length === 0) {
        return;
      }

      setIsLoadingImages(true);

      try {
        // Extract all park names from trip days and activities
        const parkLocations = new Set();
        
        // Add main destination/park
        if (trip?.destination) parkLocations?.add(trip?.destination);
        if (trip?.park) parkLocations?.add(trip?.park);
        
        // Add featured parks
        trip?.featuredParks?.forEach(park => parkLocations?.add(park));
        
        // Add parks from each day's itinerary
        trip?.days?.forEach(day => {
          day?.activities?.forEach(activity => {
            if (activity?.location) {
              parkLocations?.add(activity?.location);
            }
          });
        });

        const parkNamesArray = Array.from(parkLocations);
        console.log('🏞️ Fetching images for parks:', parkNamesArray);

        // Fetch images for all parks
        const imageMap = await getMultipleParkPhotos(parkNamesArray, {
          location: trip?.coordinates,
          maxPhotos: 1
        });

        console.log('✅ Fetched park images:', imageMap);
        setParkImages(imageMap);
      } catch (error) {
        console.error('❌ Error fetching park images:', error);
      } finally {
        setIsLoadingImages(false);
      }
    };

    // Only fetch if we don't already have images
    if (trip && Object.keys(parkImages)?.length === 0 && !isLoadingImages) {
      fetchParkImages();
    }
  }, [trip, parkImages, isLoadingImages]);

  // ✅ NEW: Helper function to get image for a specific location
  const getParkImageForLocation = (location) => {
    if (!location) return getFallbackParkImage('State Park');
    
    // Try to find exact match
    if (parkImages?.[location]) {
      return parkImages?.[location];
    }
    
    // Try to find partial match
    const matchingKey = Object.keys(parkImages)?.find(key => 
      location?.toLowerCase()?.includes(key?.toLowerCase()) ||
      key?.toLowerCase()?.includes(location?.toLowerCase())
    );
    
    if (matchingKey) {
      return parkImages?.[matchingKey];
    }
    
    // Return fallback
    return getFallbackParkImage(location);
  };

  // Helper functions
  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 3;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  // ✅ NEW: Validate that URLs are official .gov park websites
  const isOfficialParkWebsite = (url) => {
    if (!url || typeof url !== 'string') return false;
    
    try {
      const urlObj = new URL(url);
      const hostname = urlObj?.hostname?.toLowerCase();
      
      // ✅ CRITICAL: Only allow .gov domains for park and campground official websites
      return hostname?.endsWith('.gov');
    } catch (error) {
      console.warn('Invalid URL format:', url);
      return false;
    }
  };

  // ✅ NEW: Helper function to generate Google Maps search URL
  const generateGoogleMapsUrl = (placeName, parkName = null) => {
    const searchTerm = parkName 
      ? `${placeName} ${parkName}` 
      : placeName;
    const encodedQuery = encodeURIComponent(searchTerm);
    return `https://www.google.com/maps/search/?api=1&query=${encodedQuery}`;
  };

  const formatDate = (dateString, includeTime = false) => {
    if (!dateString) return 'Not specified';
    
    // ✅ CRITICAL FIX: Parse date in local time to prevent timezone offset
    // Add 'T00:00:00' to ensure date is parsed as local time, not UTC
    const dateStr = dateString?.includes('T') ? dateString : dateString + 'T00:00:00';
    const date = new Date(dateStr);
    
    if (includeTime) {
      return date?.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return date?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric', 
      month: 'long',
      day: 'numeric'
    });
  };

  const estimateMiles = (duration) => duration * 60; // Rough estimate
  const estimateTravelTime = (duration) => `${duration * 1.5} hours estimated`;
  
  const getDefaultCoordinates = (parkName) => {
    // Default coordinates for common parks
    if (parkName?.toLowerCase()?.includes('yellowstone')) return { lat: 44.4280, lng: -110.5885 };
    if (parkName?.toLowerCase()?.includes('yosemite')) return { lat: 37.8651, lng: -119.5383 };
    return { lat: 39.8283, lng: -98.5795 }; // Geographic center of US
  };

  const convertItineraryToDays = (itineraryData, startDate, aiTripData = null) => {
    // ✅ CRITICAL FIX: Add robust error handling and validation
    if (!itineraryData || !Array.isArray(itineraryData) || itineraryData?.length === 0) {
      console.warn('No itinerary data provided or empty array');
      return [];
    }

    // ✅ CRITICAL FIX: Validate start date
    if (!startDate) {
      console.warn('No start date provided, using current date');
      startDate = new Date()?.toISOString();
    }

    // ✅ NEW: Extract AI itinerary data for driving directions
    const aiItinerary = aiTripData?.itinerary || [];
    console.log('AI Itinerary data:', aiItinerary);
    
    // Group by day number with additional validation
    const groupedByDay = itineraryData?.reduce((acc, item) => {
      // ✅ CRITICAL FIX: Validate item has required fields
      if (!item || typeof item?.dayNumber !== 'number') {
        console.warn('Invalid itinerary item:', item);
        return acc;
      }

      const dayNumber = item?.dayNumber;
      if (!acc?.[dayNumber]) {
        acc[dayNumber] = [];
      }
      acc?.[dayNumber]?.push(item);
      return acc;
    }, {});

    // ✅ CRITICAL FIX: Add logging for debugging
    console.log('Grouped itinerary by day:', groupedByDay);
    console.log('Number of days with activities:', Object.keys(groupedByDay)?.length);

    // Convert to component format
    return Object.keys(groupedByDay)?.map((dayNum, index) => {
      const dayActivities = groupedByDay?.[dayNum];
      
      // ✅ CRITICAL FIX: Validate day activities
      if (!dayActivities || dayActivities?.length === 0) {
        console.warn(`Day ${dayNum} has no activities`);
        return null;
      }

      const dayDate = new Date(startDate);
      dayDate?.setDate(dayDate?.getDate() + (parseInt(dayNum) - 1));

      // ✅ CRITICAL FIX: Match with AI itinerary data using dayNumber instead of array index
      // Convert dayNum to integer and subtract 1 to get correct array index
      const aiArrayIndex = parseInt(dayNum) - 1;
      const aiDayData = aiItinerary?.[aiArrayIndex] || {};
      console.log(`Day ${dayNum} (AI array index ${aiArrayIndex}) AI data:`, aiDayData);

      // ✅ NEW: Fallback to searching by day property if array index doesn't work
      let finalAiDayData = aiDayData;
      if (!aiDayData?.drivingDirections && aiItinerary?.length > 0) {
        // Try to find matching day data by day property
        const matchedData = aiItinerary?.find(item => item?.day === parseInt(dayNum));
        if (matchedData) {
          finalAiDayData = matchedData;
          console.log(`Day ${dayNum} matched by day property:`, matchedData);
        }
      }

      // ✅ NEW: Validate park website URL to ensure it's an official .gov site
      const parkWebsiteUrl = finalAiDayData?.parkWebsite || finalAiDayData?.website || null;
      const validatedParkWebsite = parkWebsiteUrl && isOfficialParkWebsite(parkWebsiteUrl) 
        ? parkWebsiteUrl 
        : null;

      if (parkWebsiteUrl && !validatedParkWebsite) {
        console.warn(`Filtered out non-.gov park website for Day ${dayNum}:`, parkWebsiteUrl);
      }

      // ✅ UPDATED: Get image from Google Maps or fallback
      const dayLocation = dayActivities?.[0]?.location || trip?.destination || trip?.park;
      const parkImageData = getParkImageForLocation(dayLocation);

      return {
        id: `day-${dayNum}`,
        title: dayActivities?.[0]?.title || `Day ${dayNum}`,
        date: formatDate(dayDate?.toISOString()),
        weather: {
          condition: 'Partly Cloudy',
          temperature: `${Math.floor(Math.random() * 20) + 50}°F / ${Math.floor(Math.random() * 20) + 30}°F`,
          precipitation: '10% chance'
        },
        // ✅ CRITICAL FIX: Use matched AI data with better fallback logic
        drivingDirections: finalAiDayData?.drivingDirections || 
                          finalAiDayData?.directions || 
                          `Navigate to ${dayActivities?.[0]?.location || 'destination'}`,
        drivingTime: finalAiDayData?.drivingTime || 
                     finalAiDayData?.travelTime || 
                     'Travel time varies',
        distance: finalAiDayData?.distance || 
                 finalAiDayData?.distanceToTravel || 
                 'Distance not specified',
        // ✅ NEW: Only include validated .gov park websites
        parkWebsite: validatedParkWebsite,
        // ✅ UPDATED: Use Google Maps Place photo or fallback
        parkImage: parkImageData?.url || finalAiDayData?.parkImage || 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=1200&q=80',
        parkImageAlt: parkImageData?.alt || finalAiDayData?.parkImageAlt || `Scenic landscape view of ${dayLocation} state park with natural outdoor recreation areas and beautiful scenery`,
        activities: dayActivities?.map(activity => {
          // ✅ CRITICAL FIX: Preserve ALL detailed information from database
          // Build comprehensive description from multiple sources
          let fullDescription = activity?.description || '';
          
          // Add notes if they exist and are different from description
          if (activity?.notes && activity?.notes !== activity?.description) {
            fullDescription = fullDescription 
              ? `${fullDescription}\n\nAdditional Notes: ${activity?.notes}`
              : activity?.notes;
          }

          return {
            id: activity?.id,
            time: formatTime(activity?.startTime) || '9:00 AM',
            title: activity?.title,
            location: activity?.location,
            duration: activity?.endTime ? calculateActivityDuration(activity?.startTime, activity?.endTime) : '2 hours',
            icon: getActivityIcon(activity?.activityType),
            // ✅ CRITICAL FIX: Use FULL description instead of just title
            description: fullDescription || activity?.title,
            // ✅ CRITICAL FIX: Use the FULL detailed description including notes
            details: fullDescription || activity?.title,
            image: getActivityImage(activity?.activityType),
            imageAlt: `${activity?.title} activity in state park`,
            gear: getActivityGear(activity?.activityType),
            cost: activity?.cost?.toString() || '0'
          };
        })?.filter(Boolean), // ✅ CRITICAL FIX: Remove any null activities
        notes: dayActivities?.[0]?.notes || `Activities for Day ${dayNum}. Check weather conditions and park alerts before departure.`
      };
    })?.filter(Boolean); // ✅ CRITICAL FIX: Remove any null days
  };

  const formatTime = (timeString) => {
    if (!timeString) return '9:00 AM';
    const time = new Date(`2000-01-01T${timeString}`);
    return time?.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const calculateActivityDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '2 hours';
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    if (hours === 0) return `${minutes} minutes`;
    if (minutes === 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minutes`;
  };

  const getActivityIcon = (type) => {
    if (!type) return 'MapPin';
    
    const normalizedType = type?.toLowerCase()?.trim();
    
    // Comprehensive icon mapping for outdoor and state park activities
    const iconMap = {
      // Hiking & Trails
      'hiking': 'Mountain',
      'trail': 'Mountain',
      'backpacking': 'Mountain',
      'trekking': 'Mountain',
      
      // Water Activities
      'swimming': 'Waves',
      'kayaking': 'Anchor',
      'canoeing': 'Anchor',
      'boating': 'Anchor',
      'paddleboarding': 'Anchor',
      'fishing': 'Fish',
      'rafting': 'Waves',
      
      // Viewing & Observation
      'sightseeing': 'Eye',
      'wildlife viewing': 'Binoculars',
      'bird watching': 'Bird',
      'birdwatching': 'Bird',
      'stargazing': 'Moon',
      'scenic viewing': 'Eye',
      
      // Camping & Lodging
      'camping': 'Tent',
      'picnicking': 'UtensilsCrossed',
      'picnic': 'UtensilsCrossed',
      
      // Photography & Arts
      'photography': 'Camera',
      'nature photography': 'Camera',
      
      // Cycling & Wheeled
      'biking': 'Bike',
      'cycling': 'Bike',
      'mountain biking': 'Bike',
      
      // Winter Activities
      'skiing': 'Mountain',
      'snowshoeing': 'Snowflake',
      'sledding': 'Snowflake',
      
      // Climbing & Adventure
      'rock climbing': 'Mountain',
      'climbing': 'Mountain',
      'rappelling': 'Mountain',
      
      // Educational & Tours
      'ranger program': 'Info',
      'guided tour': 'Users',
      'educational program': 'BookOpen',
      'visitor center': 'Building',
      
      // Relaxation
      'relaxation': 'Coffee',
      'meditation': 'Heart',
      'yoga': 'Heart',
      
      // General Exploration
      'exploring': 'Compass',
      'exploration': 'Compass',
      'nature walk': 'TreePine',
      'walking': 'Footprints'
    };
    
    // Try exact match first
    if (iconMap?.[normalizedType]) {
      return iconMap?.[normalizedType];
    }
    
    // Try partial match for compound activity names
    for (const [key, icon] of Object.entries(iconMap)) {
      if (normalizedType?.includes(key) || key?.includes(normalizedType)) {
        return icon;
      }
    }
    
    // Default fallback
    return 'MapPin';
  };

  const getActivityImage = (type) => {
    const imageMap = {
      'hiking': 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800',
      'sightseeing': 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800',
      'camping': 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=800',
      'wildlife viewing': 'https://images.unsplash.com/photo-1695924357176-236fe86f87a6?w=800'
    };
    return imageMap?.[type?.toLowerCase()] || 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800';
  };

  const getActivityGear = (type) => {
    const gearMap = {
      'hiking': ['Hiking boots', 'Water bottle', 'Trail map'],
      'sightseeing': ['Camera', 'Binoculars', 'Comfortable shoes'],
      'camping': ['Tent', 'Sleeping bag', 'Camping stove'],
      'wildlife viewing': ['Binoculars', 'Field guide', 'Quiet clothing']
    };
    return gearMap?.[type?.toLowerCase()] || ['Camera', 'Water bottle', 'Comfortable shoes'];
  };

  useEffect(() => {
    // Set first day as expanded by default
    if (trip?.days?.length > 0) {
      setExpandedDays({ [trip?.days?.[0]?.id]: true });
    }
  }, [trip]);

  const toggleDayExpansion = (dayId) => {
    setExpandedDays((prev) => ({
      ...prev,
      [dayId]: !prev?.[dayId]
    }));
  };

  const handleEdit = () => {
    // ✅ FIXED: Use raw database dates instead of formatted display dates
    setIsEditMode(true);
    setEditedTrip({
      startDate: rawDates?.startDate || '',
      endDate: rawDates?.endDate || '',
      transportation: trip?.transportation || 'Personal Vehicle',
      accommodation: trip?.accommodation || 'Camping',
      participants: trip?.participants || 2
    });
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedTrip(null);
    setSaveError('');
  };

  const handleSaveEdit = async () => {
    if (!editedTrip) return;

    setIsSaving(true);
    setSaveError('');

    try {
      // ✅ CRITICAL FIX: Ensure dates are in YYYY-MM-DD format for PostgreSQL
      let startDate = editedTrip?.startDate;
      const endDate = editedTrip?.endDate;

      // Validate date format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex?.test(startDate) || !dateRegex?.test(endDate)) {
        throw new Error('Invalid date format. Please use YYYY-MM-DD format.');
      }

      // ✅ CRITICAL FIX: Parse dates in local time to prevent timezone offset issues
      // Add 'T00:00:00' to ensure dates are parsed as local time, not UTC
      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T00:00:00');
      const duration = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

      // Recalculate estimated cost based on duration and participants
      const basePerPersonPerDay = 50;
      const accommodationCost = editedTrip?.accommodation === 'Camping' ? 25 : 100;
      const estimatedCost = (duration * editedTrip?.participants * basePerPersonPerDay) + (duration * accommodationCost);

      // ✅ UPDATED: Send dates in YYYY-MM-DD format to database
      const updatedTripData = {
        startDate: startDate,
        endDate: endDate,
        duration: duration,
        travelers: editedTrip?.participants,
        accommodation: editedTrip?.accommodation,
        totalCost: estimatedCost
      };

      console.log('Saving trip with updates:', updatedTripData); // ✅ NEW: Debug logging

      // ✅ CRITICAL: Wait for database update to complete
      const updatedTrip = await tripService?.updateTrip(trip?.id, updatedTripData);
      
      console.log('Database update result:', updatedTrip); // ✅ NEW: Debug logging

      if (!updatedTrip) {
        throw new Error('Failed to update trip - no data returned from database');
      }

      // ✅ UPDATED: Store new raw dates AFTER successful database update
      setRawDates({
        startDate: updatedTrip?.start_date || startDate,
        endDate: updatedTrip?.end_date || endDate
      });

      // ✅ CRITICAL: Recalculate days array with new dates
      let itineraryData = await itineraryService?.getTripItinerary(trip?.id);
      const convertedDays = convertItineraryToDays(
        itineraryData, 
        updatedTrip?.start_date || startDate, 
        trip?.aiTripData
      );

      // ✅ UPDATED: Update local trip state with database-returned values AND recalculated days
      setTrip({
        ...trip,
        startDate: formatDate(updatedTrip?.start_date || startDate),
        endDate: formatDate(updatedTrip?.end_date || endDate),
        duration: updatedTrip?.duration || duration,
        participants: updatedTrip?.travelers || editedTrip?.participants,
        transportation: editedTrip?.transportation,
        accommodation: updatedTrip?.accommodation || editedTrip?.accommodation,
        estimatedCost: (updatedTrip?.total_cost || estimatedCost)?.toString(),
        lastModified: formatDate(updatedTrip?.updated_at || new Date()?.toISOString(), true),
        days: convertedDays // ✅ NEW: Update days array with new date calculations
      });

      // Exit edit mode
      setIsEditMode(false);
      setEditedTrip(null);

      // Show success message
      setShowConfirmationMessage(true);
      setTimeout(() => setShowConfirmationMessage(false), 3000);

      console.log('Trip update completed successfully'); // ✅ NEW: Success logging

    } catch (err) {
      console.error('Error saving trip updates:', err);
      setSaveError(err?.message || 'Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditedTrip(prev => {
      const updated = { ...prev, [field]: value };

      // Auto-recalculate estimated cost when date or participants change
      if (field === 'startDate' || field === 'endDate' || field === 'participants' || field === 'accommodation') {
        // ✅ CRITICAL FIX: Parse dates in local time to prevent timezone offset
        const calcStartDate = new Date((updated?.startDate || prev?.startDate) + 'T00:00:00');
        const calcEndDate = new Date((updated?.endDate || prev?.endDate) + 'T00:00:00');
        const duration = Math.ceil((calcEndDate - calcStartDate) / (1000 * 60 * 60 * 24)) + 1;
        
        const basePerPersonPerDay = 50;
        const accommodationCost = updated?.accommodation === 'Camping' ? 25 : 100;
        const estimatedCost = (duration * (updated?.participants || prev?.participants) * basePerPersonPerDay) + (duration * accommodationCost);
        
        updated.estimatedCost = estimatedCost;
      }

      return updated;
    });
  };

  const handleShare = () => {
    setToastMessage('Share Trip functionality coming soon!');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleDuplicate = () => {
    setToastMessage('Duplicate Trip functionality coming soon!');
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleGeneratePDF = async () => {
    setIsGeneratingPDF(true);
    
    try {
      // ✅ CRITICAL FIX: Expand ALL days before generating PDF
      if (trip?.days?.length > 0) {
        const allDaysExpanded = {};
        trip?.days?.forEach(day => {
          allDaysExpanded[day?.id] = true;
        });
        setExpandedDays(allDaysExpanded);
        
        // ✅ CRITICAL: Wait longer for DOM to fully render all expanded content
        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      // Generate PDF from current page
      const pdfBlob = await tripPDFService?.generateVisualSnapshotPDF('trip-detail-container');

      // Trigger download
      const url = URL?.createObjectURL(pdfBlob);
      const link = document?.createElement('a');
      link.href = url;
      link.download = `${trip?.title || trip?.destination || 'trip'}-snapshot-${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
      document?.body?.appendChild(link);
      link?.click();
      document?.body?.removeChild(link);
      URL?.revokeObjectURL(url);

      // Log activity
      await tripService?.logActivity(
        user?.id,
        trip?.id,
        'downloaded',
        'Downloaded trip snapshot PDF from detail view',
        {
          downloaded_at: new Date()?.toISOString(),
          location: 'trip_detail_view',
          type: 'visual_snapshot'
        }
      );

      // Show success message
      setShowConfirmationMessage(true);
      setTimeout(() => setShowConfirmationMessage(false), 3000);

    } catch (err) {
      console.error('PDF generation error:', err);
      setError(`Failed to generate PDF: ${err?.message}`);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleLogout = () => {
    navigate('/user-login');
  };

  // Generate structured data for the trip
  const tripStructuredData = trip ? generateTripStructuredData({
    destination: trip?.destination,
    title: trip?.trip_title,
    description: trip?.description,
    park: trip?.park,
    itinerary: trip?.itinerary_items,
    startDate: trip?.start_date,
    endDate: trip?.end_date
  }) : null;

  // Breadcrumb data
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'My Trips', path: '/user-dashboard' },
    { name: trip?.trip_title || trip?.destination || 'Trip Details', path: `/trip-detail-view/${tripId}` }
  ];

  const breadcrumbData = generateBreadcrumbStructuredData(breadcrumbs);

  // Combine structured data
  const structuredData = tripStructuredData && breadcrumbData ? {
    "@context": "https://schema.org",
    "@graph": [tripStructuredData, breadcrumbData]
  } : tripStructuredData || breadcrumbData;

  const pageTitle = trip?.trip_title || trip?.destination 
    ? `${trip?.trip_title || trip?.destination} - Trip Details | ParkTrip AI`
    : "Trip Details | ParkTrip AI";

  const pageDescription = trip?.description 
    ? `${trip?.description?.substring(0, 155)}...`
    : "View your complete state park trip itinerary with day-by-day activities, maps, weather forecasts, and packing lists.";

  // Loading state - add PDF generation check
  if (loading || isLoadingTrip) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="mx-auto mb-4 text-muted-foreground animate-spin" />
          <h2 className="text-xl font-heading font-heading-semibold text-foreground mb-2">
            {isGeneratingPDF ? 'Generating PDF...' : 'Loading Trip Details...'}
          </h2>
          <p className="text-muted-foreground">
            {isGeneratingPDF ? 'Creating your trip snapshot' : 'Please wait while we load your itinerary'}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4 text-destructive" />
          <h2 className="text-xl font-heading font-heading-semibold text-foreground mb-2">
            Unable to Load Trip
          </h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/user-dashboard')}
              className="w-full bg-primary text-primary-foreground px-4 py-2 rounded-organic hover:bg-primary/90 transition-organic"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => window.location?.reload()}
              className="w-full bg-secondary text-secondary-foreground px-4 py-2 rounded-organic hover:bg-secondary/90 transition-organic"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No trip data state
  if (!trip) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="MapPin" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-heading font-heading-semibold text-foreground mb-2">
            No Trip Found
          </h2>
          <p className="text-muted-foreground mb-4">
            The requested trip could not be found.
          </p>
          <button
            onClick={() => navigate('/user-dashboard')}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-organic hover:bg-primary/90 transition-organic"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const tripUrl = `${window.location?.origin}/trip-detail-view/${trip?.id}`;

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords="state park trip, trip planner, outdoor adventure, park itinerary"
        canonicalUrl={`https://parktripplannerai.com/trip-detail-view/${tripId}`}
        ogImage={trip?.image_url}
        structuredData={structuredData}
        noindex={!trip?.is_public}
      />

      <div id="trip-detail-container" className="min-h-screen bg-background">
        <Header
          isAuthenticated={isAuthenticated}
          user={user}
          onLogout={handleLogout}
        />
        {/* Success Message for New Trips and Edits */}
        {showConfirmationMessage && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4">
            <div className="bg-success text-success-foreground p-4 rounded-organic shadow-organic-lg border border-success/20">
              <div className="flex items-center space-x-3">
                <Icon name="CheckCircle" size={24} className="text-success-foreground" />
                <div>
                  <h3 className="font-heading font-heading-medium text-sm">
                    {isGeneratingPDF ? 'PDF Generated!' : (isEditMode ? 'Changes Saved!' : 'Trip Updated!')}
                  </h3>
                  <p className="text-xs opacity-90 mt-1">
                    {isGeneratingPDF 
                      ? 'Your trip snapshot has been downloaded successfully.' 
                      : (isEditMode 
                        ? 'Your trip details have been updated successfully.' :'Your trip has been updated successfully.')
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* ✅ NEW: Toast Message for "Coming Soon" features */}
        {showToast && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4">
            <div className="bg-primary text-primary-foreground p-4 rounded-organic shadow-organic-lg border border-primary/20">
              <div className="flex items-center space-x-3">
                <Icon name="Info" size={24} className="text-primary-foreground" />
                <div>
                  <h3 className="font-heading font-heading-medium text-sm">
                    Coming Soon
                  </h3>
                  <p className="text-xs opacity-90 mt-1">
                    {toastMessage}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Back Navigation */}
            <div className="mb-6">
              <Button
                variant="ghost"
                size="sm"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate('/user-dashboard')}
                className="text-muted-foreground hover:text-foreground">
                Back to Dashboard
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Main Content */}
              <div className="lg:col-span-3 space-y-6">
                <TripHeader
                  trip={trip}
                  onEdit={handleEdit}
                  onShare={handleShare}
                  onDuplicate={handleDuplicate}
                  onGeneratePDF={handleGeneratePDF}
                  onTitleUpdate={handleTitleUpdate}
                />

                {/* **COMBINED**: Unified Trip Overview Section */}
                <div className="bg-card border border-border rounded-organic p-6 shadow-organic">
                  {/* Header with Park Name and Activities */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-primary/10 rounded-organic flex items-center justify-center">
                        <StateIcon 
                          destination={trip?.destination || trip?.primaryPark} 
                          size={24}
                          showAbbr={false}
                        />
                      </div>
                      <div>
                        <h2 className="font-heading font-heading-bold text-xl text-card-foreground">
                          {trip?.destination || trip?.primaryPark}
                        </h2>
                        <p className="font-body text-sm text-muted-foreground">
                          {trip?.startDate} - {trip?.endDate}
                        </p>
                      </div>
                    </div>

                    {/* Edit Mode Action Buttons */}
                    {isEditMode && (
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCancelEdit}
                          disabled={isSaving}
                          iconName="X"
                          iconPosition="left"
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleSaveEdit}
                          disabled={isSaving}
                          iconName={isSaving ? "Loader" : "Check"}
                          iconPosition="left"
                        >
                          {isSaving ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Error Message */}
                  {saveError && (
                    <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-organic">
                      <p className="text-sm text-destructive">{saveError}</p>
                    </div>
                  )}

                  {/* Edit Mode Form */}
                  {isEditMode && editedTrip ? (
                    <div className="space-y-4">
                      {/* Date Range */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-card-foreground mb-2">
                            Start Date
                          </label>
                          <Input
                            type="date"
                            value={editedTrip?.startDate}
                            onChange={(e) => handleEditChange('startDate', e?.target?.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-card-foreground mb-2">
                            End Date
                          </label>
                          <Input
                            type="date"
                            value={editedTrip?.endDate}
                            onChange={(e) => handleEditChange('endDate', e?.target?.value)}
                            min={editedTrip?.startDate}
                            className="w-full"
                          />
                        </div>
                      </div>

                      {/* ✅ NEW: Duration Preview */}
                      <div className="p-3 bg-primary/10 rounded-organic">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-card-foreground">
                            Trip Duration:
                          </span>
                          <span className="text-lg font-bold text-primary">
                            {(() => {
                              const calcStartDate = new Date(editedTrip?.startDate);
                              const calcEndDate = new Date(editedTrip?.endDate);
                              const days = Math.ceil((calcEndDate - calcStartDate) / (1000 * 60 * 60 * 24)) + 1;
                              const nights = days > 0 ? days - 1 : 0;
                              return days === 1 ? '1 Day' : (nights === 0 ? `${days} Days` : `${days} Days / ${nights} ${nights === 1 ? 'Night' : 'Nights'}`);
                            })()}
                          </span>
                        </div>
                      </div>

                      {/* Transportation */}
                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-2">
                          Transportation
                        </label>
                        <Select
                          value={editedTrip?.transportation}
                          onChange={(newValue) => handleEditChange('transportation', newValue)}
                          options={transportationOptions}
                          placeholder="Select transportation"
                          className="w-full"
                        />
                      </div>

                      {/* Accommodation */}
                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-2">
                          Accommodation
                        </label>
                        <Select
                          value={editedTrip?.accommodation}
                          onChange={(newValue) => handleEditChange('accommodation', newValue)}
                          options={accommodationOptions}
                          placeholder="Select accommodation"
                          className="w-full"
                        />
                      </div>

                      {/* Number of Participants */}
                      <div>
                        <label className="block text-sm font-medium text-card-foreground mb-2">
                          Number of Participants
                        </label>
                        <Input
                          type="number"
                          min="1"
                          max="20"
                          value={editedTrip?.participants}
                          onChange={(e) => handleEditChange('participants', parseInt(e?.target?.value) || 1)}
                          className="w-full"
                        />
                      </div>

                      {/* Estimated Cost Preview */}
                      <div className="p-4 bg-primary/10 rounded-organic">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-card-foreground">
                            Updated Estimated Cost:
                          </span>
                          <span className="text-lg font-bold text-primary">
                            ${editedTrip?.estimatedCost || 0}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          Based on {Math.ceil((new Date(editedTrip?.endDate) - new Date(editedTrip?.startDate)) / (1000 * 60 * 60 * 24)) + 1} days and {editedTrip?.participants} participants
                        </p>
                      </div>
                    </div>
                  ) : (
                    // View Mode - Combined Display
                    <>
                      {/* Basic Trip Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="text-center p-3 bg-muted/30 rounded-organic">
                          <Icon name="Users" size={20} color="var(--color-success)" className="mx-auto mb-1" />
                          <p className="font-caption text-xs text-muted-foreground">Participants</p>
                          <p className="font-body font-body-medium text-sm text-card-foreground">{trip?.participants || 2}</p>
                        </div>
                        <div className="text-center p-3 bg-muted/30 rounded-organic">
                          <Icon name="DollarSign" size={20} color="var(--color-secondary)" className="mx-auto mb-1" />
                          <p className="font-caption text-xs text-muted-foreground">Est. Cost</p>
                          <p className="font-body font-body-medium text-sm text-card-foreground">
                            ${trip?.estimatedCost || '500'}
                          </p>
                        </div>
                        <div className="text-center p-3 bg-muted/30 rounded-organic">
                          <Icon name="Home" size={20} color="var(--color-accent)" className="mx-auto mb-1" />
                          <p className="font-caption text-xs text-muted-foreground">Accommodation</p>
                          <p className="font-body font-body-medium text-sm text-card-foreground">{trip?.accommodation || 'Camping'}</p>
                        </div>
                        <div className="text-center p-3 bg-muted/30 rounded-organic">
                          <Icon name="Car" size={20} color="var(--color-primary)" className="mx-auto mb-1" />
                          <p className="font-caption text-xs text-muted-foreground">Transportation</p>
                          <p className="font-body font-body-medium text-sm text-card-foreground">{trip?.transportation || 'Personal Vehicle'}</p>
                        </div>
                      </div>
                      {/* Activity Types Tags */}
                      {trip?.activityTypes?.length > 0 && (
                        <div className="mb-6">
                          <h3 className="font-heading font-heading-medium text-sm text-card-foreground mb-2">
                            Activities:
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            {trip?.activityTypes?.map((activity, index) => {
                              // Map activity name to icon
                              const activityIconName = getActivityIcon(activity?.toLowerCase());
                              
                              return (
                                <span
                                  key={index}
                                  className="bg-accent/10 text-accent text-xs px-3 py-1.5 rounded-organic flex items-center gap-1.5"
                                >
                                  <Icon name={activityIconName} size={14} className="text-accent" />
                                  <span>{activity}</span>
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Featured Parks Section - Only show if multiple parks */}
                      {trip?.featuredParks?.length > 1 && (
                        <div className="mb-6 pb-6 border-b border-border">
                          <h3 className="font-heading font-heading-medium text-sm text-card-foreground mb-3">
                            Featured Parks:
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {trip?.featuredParks?.map((park, index) => {
                              const parkWebsite = trip?.reservationInfo?.officialParkWebsites?.find(site => 
                                site?.parkName?.toLowerCase()?.includes(park?.toLowerCase()?.split(' ')?.[0])
                              );
                              
                              // ✅ NEW: Validate park website URL
                              const validWebsite = parkWebsite?.website && isOfficialParkWebsite(parkWebsite?.website) 
                                ? parkWebsite?.website 
                                : null;
                              const validReservationLink = parkWebsite?.reservationLink && isOfficialParkWebsite(parkWebsite?.reservationLink)
                                ? parkWebsite?.reservationLink
                                : null;
                              
                              // ✅ NEW: Generate Google Maps link for this park
                              const googleMapsUrl = generateGoogleMapsUrl(park);
                              
                              return (
                                <div key={index} className="bg-primary/10 p-3 rounded-organic">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-primary text-sm font-medium">{park}</span>
                                    <div className="flex space-x-2">
                                      {/* ✅ NEW: Google Maps link */}
                                      <a
                                        href={googleMapsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center space-x-1 text-success hover:underline text-xs"
                                        title={`View ${park} on Google Maps`}
                                      >
                                        <Icon name="MapPin" size={12} />
                                        <span>Maps</span>
                                      </a>
                                      {validWebsite && (
                                        <a
                                          href={validWebsite}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center space-x-1 text-primary hover:underline text-xs"
                                        >
                                          <Icon name="ExternalLink" size={12} />
                                          <span>Info</span>
                                        </a>
                                      )}
                                      {validReservationLink && (
                                        <a
                                          href={validReservationLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center space-x-1 text-warning hover:underline text-xs"
                                        >
                                          <Icon name="Calendar" size={12} />
                                          <span>Reserve</span>
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                  {parkWebsite?.phone && (
                                    <p className="text-xs text-muted-foreground">
                                      📞 {parkWebsite?.phone}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {/* Accommodation Information Section */}
                      {trip?.accommodations && (
                        <div className="mb-6 pb-6 border-b border-border">
                          <h3 className="font-heading font-heading-medium text-sm text-card-foreground mb-3">
                            Accommodation Options:
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Campgrounds */}
                            {trip?.accommodations?.campgrounds?.length > 0 && (
                              <div className="p-4 bg-muted/20 rounded-organic">
                                <div className="flex items-center space-x-2 mb-2">
                                  <Icon name="Tent" size={16} color="var(--color-green)" />
                                  <h4 className="font-body font-body-medium text-sm text-card-foreground">Campgrounds</h4>
                                </div>
                                {trip?.accommodations?.campgrounds?.slice(0, 2)?.map((campground, index) => {
                                  // ✅ NEW: Validate campground website URL
                                  const validCampgroundWebsite = campground?.website && isOfficialParkWebsite(campground?.website)
                                    ? campground?.website
                                    : null;
                                  
                                  // ✅ NEW: Generate Google Maps link for campground
                                  const googleMapsUrl = generateGoogleMapsUrl(
                                    campground?.name, 
                                    trip?.destination || trip?.primaryPark
                                  );
                                  
                                  return (
                                    <div key={index} className="mb-3 last:mb-0">
                                      <div className="flex items-center justify-between mb-1">
                                        <p className="font-caption text-xs font-medium text-card-foreground">{campground?.name}</p>
                                        <div className="flex space-x-2">
                                          {/* ✅ NEW: Google Maps link */}
                                          <a
                                            href={googleMapsUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center space-x-1 text-success hover:underline text-xs"
                                            title={`View ${campground?.name} on Google Maps`}
                                          >
                                            <Icon name="MapPin" size={10} />
                                            <span>Maps</span>
                                          </a>
                                          {validCampgroundWebsite && (
                                            <a
                                              href={validCampgroundWebsite}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="flex items-center space-x-1 text-primary hover:underline text-xs"
                                            >
                                              <Icon name="ExternalLink" size={10} />
                                              <span>Details</span>
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                      <p className="font-caption text-xs text-muted-foreground">{campground?.location}</p>
                                      <p className="font-caption text-xs text-success">{campground?.cost}</p>
                                      {campground?.amenities?.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {campground?.amenities?.slice(0, 3)?.map((amenity, aIndex) => (
                                            <span key={aIndex} className="px-1 py-0.5 bg-success/10 text-success text-xs rounded-organic-sm">
                                              {amenity}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Cabins */}
                            {trip?.accommodations?.cabins?.length > 0 && (
                              <div className="p-4 bg-muted/20 rounded-organic">
                                <div className="flex items-center space-x-2 mb-2">
                                  <Icon name="Home" size={16} color="var(--color-orange)" />
                                  <h4 className="font-body font-body-medium text-sm text-card-foreground">Cabins & Lodges</h4>
                                </div>
                                {trip?.accommodations?.cabins?.slice(0, 2)?.map((cabin, index) => {
                                  // ✅ NEW: Validate cabin website URL
                                  const validCabinWebsite = cabin?.website && isOfficialParkWebsite(cabin?.website)
                                    ? cabin?.website
                                    : null;
                                  
                                  // ✅ NEW: Generate Google Maps link for cabin
                                  const googleMapsUrl = generateGoogleMapsUrl(
                                    cabin?.name,
                                    trip?.destination || trip?.primaryPark
                                  );
                                  
                                  return (
                                    <div key={index} className="mb-3 last:mb-0">
                                      <div className="flex items-center justify-between mb-1">
                                        <p className="font-caption text-xs font-medium text-card-foreground">{cabin?.name}</p>
                                        <div className="flex space-x-2">
                                          {/* ✅ NEW: Google Maps link */}
                                          <a
                                            href={googleMapsUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center space-x-1 text-success hover:underline text-xs"
                                            title={`View ${cabin?.name} on Google Maps`}
                                          >
                                            <Icon name="MapPin" size={10} />
                                            <span>Maps</span>
                                          </a>
                                          {validCabinWebsite && (
                                            <a
                                              href={validCabinWebsite}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="flex items-center space-x-1 text-primary hover:underline text-xs"
                                            >
                                              <Icon name="ExternalLink" size={10} />
                                              <span>Details</span>
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                      <p className="font-caption text-xs text-muted-foreground">Capacity: {cabin?.capacity}</p>
                                      <p className="font-caption text-xs text-warning">{cabin?.cost}</p>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Dining Information Section */}
                      {trip?.dining && (
                        <div className="mb-6 pb-6 border-b border-border">
                          <h3 className="font-heading font-heading-medium text-sm text-card-foreground mb-3">
                            Dining Options:
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {trip?.dining?.onSite?.length > 0 && (
                              <div className="p-3 bg-muted/20 rounded-organic">
                                <h4 className="font-caption text-xs font-medium text-card-foreground mb-2">On-Site Dining:</h4>
                                <div className="space-y-1">
                                  {trip?.dining?.onSite?.map((option, index) => (
                                    <p key={index} className="font-caption text-xs text-muted-foreground">• {option}</p>
                                  ))}
                                </div>
                              </div>
                            )}
                            {trip?.dining?.nearby?.length > 0 && (
                              <div className="p-3 bg-muted/20 rounded-organic">
                                <h4 className="font-caption text-xs font-medium text-card-foreground mb-2">Nearby Restaurants:</h4>
                                <div className="space-y-2">
                                  {trip?.dining?.nearby?.slice(0, 3)?.map((option, index) => {
                                    // Create Google Maps search URL for the restaurant
                                    const searchQuery = encodeURIComponent(`${option} near ${trip?.destination || trip?.primaryPark}`);
                                    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${searchQuery}`;
                                    
                                    return (
                                      <div key={index} className="flex items-center justify-between">
                                        <p className="font-caption text-xs text-muted-foreground">• {option}</p>
                                        <a
                                          href={googleMapsUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center space-x-1 text-primary hover:underline text-xs"
                                          title={`View ${option} on Google Maps`}
                                        >
                                          <Icon name="MapPin" size={12} />
                                          <span>Maps</span>
                                        </a>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      {/* Reservation Information Section */}
                      {trip?.reservationInfo && (
                        <div className="p-4 bg-primary/10 rounded-organic">
                          <div className="flex items-center space-x-2 mb-3">
                            <Icon name="Calendar" size={16} color="var(--color-primary)" />
                            <h3 className="font-heading font-heading-medium text-sm text-card-foreground">Reservation Information</h3>
                          </div>
                          
                          {/* Main Reservation System */}
                          {trip?.reservationInfo?.website && isOfficialParkWebsite(trip?.reservationInfo?.website) && (
                            <div className="mb-3 p-3 bg-white/50 rounded-organic">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-caption text-xs font-medium text-card-foreground">Main Reservation System</p>
                                  <p className="font-caption text-xs text-muted-foreground">Centralized booking for multiple parks</p>
                                </div>
                                <a
                                  href={trip?.reservationInfo?.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center space-x-1 bg-primary text-primary-foreground px-2 py-1 rounded-organic text-xs hover:bg-primary/90 transition-colors"
                                >
                                  <Icon name="ExternalLink" size={12} />
                                  <span>Book Now</span>
                                </a>
                              </div>
                            </div>
                          )}

                          {/* Individual Park Websites - Only show if not already shown in Featured Parks */}
                          {trip?.reservationInfo?.officialParkWebsites?.length > 0 && trip?.featuredParks?.length <= 1 && (
                            <div className="space-y-2">
                              <p className="font-caption text-xs font-medium text-card-foreground">Park Information:</p>
                              {trip?.reservationInfo?.officialParkWebsites?.map((parkSite, index) => {
                                // ✅ NEW: Validate park site URLs
                                const validParkWebsite = parkSite?.website && isOfficialParkWebsite(parkSite?.website)
                                  ? parkSite?.website
                                  : null;
                                const validReservationLink = parkSite?.reservationLink && isOfficialParkWebsite(parkSite?.reservationLink)
                                  ? parkSite?.reservationLink
                                  : null;
                                
                                // ✅ NEW: Generate Google Maps link for park
                                const googleMapsUrl = generateGoogleMapsUrl(parkSite?.parkName);
                                
                                // Only render if at least one valid link exists or we have park name for maps
                                if (!validParkWebsite && !validReservationLink && !parkSite?.parkName) return null;
                                
                                return (
                                  <div key={index} className="p-3 bg-white/30 rounded-organic">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="font-caption text-xs font-medium text-card-foreground">{parkSite?.parkName}</p>
                                        {parkSite?.phone && (
                                          <p className="font-caption text-xs text-muted-foreground">📞 {parkSite?.phone}</p>
                                        )}
                                      </div>
                                      <div className="flex space-x-2">
                                        {/* ✅ NEW: Google Maps link */}
                                        <a
                                          href={googleMapsUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center space-x-1 text-success hover:underline text-xs"
                                          title={`View ${parkSite?.parkName} on Google Maps`}
                                        >
                                          <Icon name="MapPin" size={12} />
                                          <span>Maps</span>
                                        </a>
                                        {validParkWebsite && (
                                          <a
                                            href={validParkWebsite}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center space-x-1 text-primary hover:underline text-xs"
                                          >
                                            <Icon name="Globe" size={12} />
                                            <span>Info</span>
                                          </a>
                                        )}
                                        {validReservationLink && (
                                          <a
                                            href={validReservationLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center space-x-1 bg-warning text-warning-foreground px-2 py-1 rounded-organic text-xs hover:bg-warning/90 transition-colors"
                                          >
                                            <Icon name="Calendar" size={12} />
                                            <span>Reserve</span>
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Weather Forecast - If Available */}
                      {trip?.weatherForecast && (
                        <div className="mt-4 p-3 bg-muted/30 rounded-organic">
                          <div className="flex items-center space-x-2">
                            <Icon name="Sun" size={16} color="var(--color-warning)" />
                            <span className="font-caption text-xs text-muted-foreground">Weather Forecast:</span>
                            <span className="font-body text-sm text-card-foreground">
                              {trip?.weatherForecast}
                            </span>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* ✅ NEW: Enhanced Weather Forecast Section */}
                <WeatherForecast 
                  trip={trip}
                  startDate={rawDates?.startDate}
                  endDate={rawDates?.endDate}
                />

                {/* Day Itineraries */}
                <div className="space-y-4">
                  <h2 className="font-heading font-heading-bold text-2xl text-foreground flex items-center gap-2">
                    <Icon name="Calendar" size={24} />
                    Trip Itinerary
                  </h2>
                  
                  {trip?.days?.map((day, index) => (
                    <DayItinerary
                      key={day?.id}
                      day={day}
                      dayNumber={index + 1}
                      isExpanded={expandedDays?.[day?.id]}
                      onToggleExpand={() => toggleDayExpansion(day?.id)} />
                  ))}

                  {(!trip?.days || trip?.days?.length === 0) && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Icon name="Calendar" size={48} className="mx-auto mb-4 opacity-50" />
                      <p>No itinerary details available for this trip.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1 space-y-6">
                <TripMetadata trip={trip} />
                <TripMap trip={trip} />
                <PackingList trip={trip} />
              </div>
            </div>
          </div>
        </main>
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          tripUrl={tripUrl}
          tripTitle={trip?.title}
          tripId={trip?.id}
        />
      </div>
    </>
  );
};

export default TripDetailView;